import { tryParseDate } from '@shared/functions/try-parse-date';
import { BcmUser } from '@modules/bcm/bcm-user';
import { BcmPaymentType, BcmPaymentTypeFromString, IPerson, paymentTypeTranslationKeys, Person } from '@shared/models/person';
import { Company, ICompany } from '@shared/models/company';
import { IInvoice, Invoice } from '@shared/models/invoice';
import { BcmReceipt, BcmReceiptDto } from '@shared/models/bcm-receipt';
import { financialRecordTypeTranslationsDe, InvoiceTypes, QuoteDocumentType, ReceiptTypes } from '@shared/invoice-types';
import { Quote, QuoteDto } from '@shared/models/quote';
import { Tenant } from '@shared/models/tenant';
import { InvoicePosition } from '@shared/models/invoice-position';
import { BcmFile } from '@shared/models/bcm-file';
import { BcmSettings } from '@shared/models/bcm-settings';
import { BcmInvoiceWdTemplate } from '@shared/models/bcm-invoice-wd-template';
import { toDateStringDE } from '@core/functions/to-date-string';
import { TranslationService } from '@core/translation/translation.service';
import { v4 as uuidv4 } from 'uuid';

export enum FinancialRecordStatus {
    Open = 'open',
    Reopen = 'reopen',
    Payed = 'payed',
    PaidPartially = 'paid_partially',
    Canceled = 'canceled',
    Error = 'error',
    Done = 'done',
    BookedOut = 'booked_out',
}

export const financialRecordStatusTranslationsGer: { [key in FinancialRecordStatus]: string } = {
    [FinancialRecordStatus.Open]: 'accounting.financialRecords.statusValues.open',
    [FinancialRecordStatus.Payed]: 'accounting.financialRecords.statusValues.payed',
    [FinancialRecordStatus.PaidPartially]: 'accounting.financialRecords.statusValues.paidPartially',
    [FinancialRecordStatus.Canceled]: 'accounting.financialRecords.statusValues.canceled',
    [FinancialRecordStatus.Error]: 'accounting.financialRecords.statusValues.error',
    [FinancialRecordStatus.Done]: 'accounting.financialRecords.statusValues.done',
    [FinancialRecordStatus.Reopen]: 'accounting.financialRecords.statusValues.reopen',
    [FinancialRecordStatus.BookedOut]: 'accounting.financialRecords.statusValues.bookedOut',
};

export interface BcmFinancialRecordPdfData {
    testRun?: boolean;
    tenantRelation: string;
    debtorNumber: string;
    invoiceNumber: string;
    bcmSettings: BcmSettings;
    paymentType: BcmPaymentType;
    tenant: Tenant;
    address: string;
    date: Date;
    dueDate?: Date;
    note?: string;
    positions: InvoicePosition[];
    priceData: TotalPrices;
    invoiceLogo: BcmFile;
    iban: string;
    qrData?: string;
    wordTemplate?: BcmInvoiceWdTemplate;
}

export interface TotalPrices {
    totalNetPrice: number;
    totalGrossPrice: number;
    totalTaxes: { [key: number]: number };
    totalTax?: number;
    totalTaxFreeProductsPrice?: number;
}

export interface BcmFinancialRecordDto {
    number: string;

    invoice?: IInvoice;
    receipt?: BcmReceiptDto;
    quote?: QuoteDto;
    bcm_quotes_id?: number | null;

    person?: IPerson;
    company?: ICompany;

    customerName: string;
    address: string;

    type: InvoiceTypes | ReceiptTypes | QuoteDocumentType;
    status: FinancialRecordStatus;
    paymentType?: BcmPaymentType | string;

    cancellationRecord?: BcmFinancialRecordDto;
    cancellationNumber?: string;
    cancellationReason?: string;

    reopenReason: string;
    reopenDescription: string;

    date: Date;
    dueDate: Date;

    insertedOn?: string;
    createdBy?: BcmUser;

    total: number;
    creatorName: string;
    isSEPA: number;
    isEInvoice?: boolean;
}

export class BcmFinancialRecord {

    static fakeId = 1;

    id: number;
    number: string;

    invoice?: Invoice;
    receipt?: BcmReceipt;
    quote?: Quote;
    bcmQuotesId?: number | null;

    person?: Person;
    company?: Company;

    customerName: string;
    address: string;

    type: InvoiceTypes | ReceiptTypes | QuoteDocumentType;
    status: FinancialRecordStatus;

    cancellationRecord?: BcmFinancialRecord;
    cancellationNumber?: string;
    cancellationReason?: string;

    reopenReason: string;
    reopenDescription: string;

    date: Date;
    dueDate: Date;

    insertedOn?: Date;
    createdBy: BcmUser;

    paymentTypeKey: string;

    total: number;
    creatorName: string;

    // helpers
    numberTypeAbbreviation?: 'G' | 'S' | 'SG';
    numberTooltip?: string;

    statusText?: string;
    statusTooltip?: string;
    typeText?: string;

    isSEPA: boolean;

    isEInvoice?: boolean;

    uuid: string;

    constructor(dto: BcmFinancialRecordDto) {
        this.id = BcmFinancialRecord.fakeId++;
        this.number = dto.number;

        this.invoice = dto.invoice ? new Invoice(dto.invoice) : null;
        this.receipt = dto.receipt ? new BcmReceipt(dto.receipt) : null;
        this.quote = dto.quote ? new Quote(dto.quote) : null;
        this.bcmQuotesId = dto.bcm_quotes_id != null ? Number(dto.bcm_quotes_id) : null;

        this.person = dto.person ? new Person(dto.person) : null;
        this.company = dto.company ? new Company(dto.company) : null;

        if (this.invoice?.id) {
            this.invoice.person = this.person;
            this.invoice.company = this.company;
        }

        this.customerName = dto.customerName;
        this.address = dto.address;

        this.type = dto.type;
        this.status = dto.status;

        this.cancellationRecord = dto.cancellationRecord ? new BcmFinancialRecord(dto.cancellationRecord) : null;
        this.cancellationNumber = dto.cancellationNumber || null;
        this.cancellationReason = this.invoice?.cancellationReason || this.receipt?.cancellationReason || null;

        this.reopenReason = dto.reopenReason;
        this.reopenDescription = dto.reopenDescription;

        this.date = tryParseDate(dto.date);
        this.dueDate = tryParseDate(dto.dueDate);

        this.insertedOn = tryParseDate(dto.insertedOn);
        this.createdBy = dto.createdBy ? new BcmUser(dto.createdBy) : null;

        this.paymentTypeKey = paymentTypeTranslationKeys[this.resolvePaymentType(dto)];

        this.total = dto.total;
        this.creatorName = dto.creatorName;
        this.isSEPA = dto.isSEPA === 1;
        this.isEInvoice = dto.isEInvoice === true || dto.invoice?.eInvoice === true;

        this.setupHelpers();

        this.uuid = uuidv4();
    }

    public get reopenDescriptionText(): string {
        return (this.reopenReason === 'return-debit' ? 'Rücklastschrift' : (this.reopenDescription?.length > 0 ? this.reopenDescription : 'Zahlung fehlgeschlagen'));
    }

    public setupHelpers() {
        this.setupNumberHelpers();
        this.setupStatusHelpers();
        this.setupTypeHelpers();
    }

    private setupNumberHelpers() {
        if (this.invoice != null) {
            switch (this.invoice.invoiceType) {
                case InvoiceTypes.Crediting:
                    this.numberTypeAbbreviation = 'G';
                    this.numberTooltip = 'Gutschrift';
                    break;
                case InvoiceTypes.CancellationInvoice:
                    this.numberTypeAbbreviation = 'S';
                    this.numberTooltip = TranslationService.translate('accounting.financialDocuments.cancellationInvoice');
                    break;
                case InvoiceTypes.CancellationCrediting:
                    this.numberTypeAbbreviation = 'SG';
                    this.numberTooltip = 'Stornogutschrift';
                    break;
            }
        }
        if (this.receipt != null) {
            switch (this.receipt.receiptType) {
                case 'cancellationReceipt':
                    this.numberTypeAbbreviation = 'S';
                    this.numberTooltip = TranslationService.translate('accounting.financialDocuments.cancellationInvoice');
                    break;
            }
        }
    }

    private setupStatusHelpers() {
        if (this.type === QuoteDocumentType.Quote) {
            this.statusText = `accounting.quotes.status.${this.status}`;
            return;
        }

        this.statusText = financialRecordStatusTranslationsGer[this.status] || undefined;

        switch (this.status) {
            case FinancialRecordStatus.Reopen:
                this.statusTooltip = this.reopenDescriptionText;
                break;
            case FinancialRecordStatus.Payed:
                if (this.invoice?.payedOn) {
                    this.statusTooltip = 'bezahlt am ' + toDateStringDE(this.invoice.payedOn);
                }
                break;
        }
    }

    private setupTypeHelpers() {
        this.typeText = financialRecordTypeTranslationsDe[this.type] || '';
    }

    /**
     * List rows expose paymentType on the financial record (view bcm_financial_records).
     * enrichFinancialRecordPaymentAmounts also adds a stub invoice { missingAmount: 0 } for receipts;
     * that stub must not override the receipt payment type via Invoice defaults.
     */
    private resolvePaymentType(dto: BcmFinancialRecordDto): BcmPaymentType | undefined {
        if (dto.paymentType) {
            return BcmPaymentTypeFromString[dto.paymentType as string] ?? dto.paymentType as BcmPaymentType;
        }

        return this.receipt?.paymentType ?? this.invoice?.paymentType;
    }
}


